/* eslint-disable */

import React, { useState, useEffect } from 'react'
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemButton, ListItemText, Divider, Typography } from '@mui/material'
import SkeinIcon from './SkeinIcon'
import { api } from '../requests/axios'

interface Colorway {
  id: number
  name: string
  hex_color: string
  hex_colors?: string[]
  yarn?: {
    id: number
    name: string
    brand: string
  }
}

interface SelectColorwayDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (colorwayId: number) => void
  onCreateNew: () => void
  yarnId: number
  existingColorwayIds?: number[]
}

export default function SelectColorwayDialog({ open, onClose, onSelect, onCreateNew, yarnId, existingColorwayIds = [] }: SelectColorwayDialogProps) {
  const [colorways, setColorways] = useState<Colorway[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && yarnId) {
      fetchColorways()
    }
  }, [open, yarnId])

  const fetchColorways = async () => {
    setLoading(true)
    try {
      const response = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${yarnId}/colorways.json`)
      setColorways(response.data || [])
    } catch (error: any) {
      console.error('Error fetching colorways:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (colorwayId: number) => {
    onSelect(colorwayId)
    onClose()
  }

  const existingColorways = colorways.filter(c => existingColorwayIds.includes(c.id))
  const newColorways = colorways.filter(c => !existingColorwayIds.includes(c.id))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Existing Colorway</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading colorways...</Typography>
        ) : (
          <Box>
            {existingColorways.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Previously Used
                </Typography>
                <List>
                  {existingColorways.map((colorway) => (
                    <ListItem key={colorway.id} disablePadding>
                      <ListItemButton onClick={() => handleSelect(colorway.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <SkeinIcon color={colorway.hex_colors?.length ? colorway.hex_colors : colorway.hex_color} size={40} />
                          <ListItemText 
                            primary={colorway.name}
                            secondary={colorway.hex_colors?.length ? colorway.hex_colors.map(c => `#${c.replace('#', '')}`).join(', ') : `#${colorway.hex_color.replace('#', '')}`}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {existingColorways.length > 0 && newColorways.length > 0 && <Divider sx={{ my: 2 }} />}
            
            {newColorways.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Available Colorways
                </Typography>
                <List>
                  {newColorways.map((colorway) => (
                    <ListItem key={colorway.id} disablePadding>
                      <ListItemButton onClick={() => handleSelect(colorway.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <SkeinIcon color={colorway.hex_colors?.length ? colorway.hex_colors : colorway.hex_color} size={40} />
                          <ListItemText 
                            primary={colorway.name}
                            secondary={colorway.hex_colors?.length ? colorway.hex_colors.map(c => `#${c.replace('#', '')}`).join(', ') : `#${colorway.hex_color.replace('#', '')}`}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {colorways.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No existing colorways found for this yarn.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => { 
          // Don't call onClose here - let onCreateNew handle closing the dialog
          // This prevents clearing selectedYarnForColorway before it's needed
          onCreateNew(); 
        }} variant="outlined">Create New Colorway</Button>
      </DialogActions>
    </Dialog>
  )
}
